import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckboxOptionType } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { DataPair } from "../../types";
import { downloadFile } from "../../utils/fileDownload";
import { mapSelectOptions } from "../../utils/map4select";
import { productionCodeKeys } from "./productionCode.keys";
import { productionCodeService } from "./productionCode.service";
import {
  CreateProductionCodeResponse,
  DownloadProductionCodeRequestFilePayload,
  ProductionCodeObject,
  ProductionCodeRequestApprovalPayload,
  ProductionCodeRequestFormPayload,
  ProductionCodeRequestListPayload,
  ProdukcioniKodIzvodPayload,
  ProdukcioniKodIzvodResponse,
} from "./productionCode.types";

export function useProductionCodeRequestList({
  filters,
  enabled,
}: {
  filters: ProductionCodeRequestListPayload;
  enabled?: boolean;
}) {
  return useQuery({
    ...productionCodeKeys.productionCodeRequestList({ filters }),
    queryFn: () => productionCodeService.getAllProductionCodeRequest(filters),
    select: (response) => response?.data,
    retry: false,
    enabled,
  });
}

export function useProdukcioniKodoviListaIzvoda({
  filters,
  enabled,
}: {
  filters: ProdukcioniKodIzvodPayload;
  enabled?: boolean;
}) {
  return useQuery({
    ...productionCodeKeys.produkcioniKodoviListaIzvoda({ filters }),
    queryFn: () =>
      productionCodeService.getAllProdukcioniKodoviListaIzvoda(filters),
    select: (response) =>
      ({
        list: response?.data?.list ?? [],
        total: response?.data?.total ?? 0,
      } as ProdukcioniKodIzvodResponse),
    retry: false,
    enabled,
  });
}

export function useProductionCodeRequestDetails({
  id,
  enabled,
}: {
  id: number | null;
  enabled?: boolean;
}) {
  return useQuery({
    ...productionCodeKeys.productionCodeRequestDetails(id),
    queryFn: () => productionCodeService.getProductionCodeRequestDetails(id),
    select: (response) => response?.data,
    retry: false,
    enabled,
  });
}

export function useCreateProductionCodeRequest({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: CreateProductionCodeResponse | null) => void;
  onError?: (error: Error | string) => void;
} = {}) {
  return useMutation({
    mutationFn: async (payload: ProductionCodeRequestFormPayload) => {
      const response = await productionCodeService.createProductionCodeRequest(
        payload
      );

      if (response?.errorMessage) {
        onError?.(response?.errorMessage);
      }

      return response?.data;
    },
    onError,
    onSuccess: (data: CreateProductionCodeResponse | null) => {
      if (data) {
        onSuccess?.(data);
      }
    },
    retry: false,
  });
}

export function useProductionCodeRequestFormData({
  tipObveznikaId,
  enabled,
}: {
  tipObveznikaId: number | null;
  enabled?: boolean;
}) {
  return useQuery({
    ...productionCodeKeys.formData(tipObveznikaId),
    queryFn: () =>
      productionCodeService.getProductionCodeRequestFormData(tipObveznikaId),
    select: (response) => ({
      obveznikTypes: mapSelectOptions<DataPair>({
        labelAccessor: "naziv",
        valueAccessor: "id",
        sourceArray: response?.data?.tipObveznika,
      }) as CheckboxOptionType[],
      productTypes: mapSelectOptions<DataPair>({
        labelAccessor: "naziv",
        valueAccessor: "id",
        sourceArray: response?.data?.vrstaProizvoda,
      }),
      objects: mapSelectOptions<ProductionCodeObject, { masine: DataPair[] }>({
        labelAccessor: "naziv",
        valueAccessor: "id",
        sourceArray: response?.data?.objekti,
        keepFields: ["masine"],
      }) as Array<DefaultOptionType & { masine: DataPair[] }>,
    }),
    retry: false,
    enabled,
  });
}

export function useDownloadRequestProductionCodes({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error | string) => void;
} = {}) {
  return useMutation({
    mutationFn: async (payload: DownloadProductionCodeRequestFilePayload) => {
      const response =
        await productionCodeService.downloadRequestProductionCodes(payload);

      if (response?.errorMessage) {
        onError?.(response?.errorMessage);
      }

      return response?.data;
    },
    onError,
    onSuccess: (data, variables) => {
      if (data) {
        onSuccess?.();

        downloadFile(
          data,
          `Zahtev_${
            variables.docName ?? variables.zahtevId ?? ""
          }_SifraBrenda_${variables.sifraRobneMarke ?? ""}_ProdukcioniKodovi`,
          "xlsx"
        );
      }
    },
    retry: false,
  });
}

export function useDownloadRequestProformaInvoice({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error | string) => void;
} = {}) {
  return useMutation({
    mutationFn: async (payload: DownloadProductionCodeRequestFilePayload) => {
      const response =
        await productionCodeService.downloadRequestProformaInvoice(payload);

      if (response?.errorMessage) {
        onError?.(response?.errorMessage);
      }

      return response?.data?.content;
    },
    onError,
    onSuccess: (data, variables) => {
      if (data) {
        onSuccess?.();

        downloadFile(
          data,
          `Zahtev_${
            variables.docName ?? variables.zahtevId ?? ""
          }_SifraBrenda_${variables.sifraRobneMarke ?? ""}_Predracun`,
          "pdf"
        );
      }
    },
    retry: false,
  });
}

export function useAcceptProductionCodeRequest({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error | string) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ProductionCodeRequestApprovalPayload) => {
      const response = await productionCodeService.acceptProductionCodeRequest(
        payload
      );

      if (response?.errorMessage) {
        onError?.(response?.errorMessage);
      }

      return response?.data;
    },
    onError,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        predicate(query) {
          return (
            query?.queryKey?.[1] === "productionCodeRequestDetails" &&
            query?.queryKey?.[0] === "production_code"
          );
        },
      });

      onSuccess?.();
    },
    retry: false,
  });
}

export function useDeclineProductionCodeRequest({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error | string) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ProductionCodeRequestApprovalPayload) => {
      const response = await productionCodeService.declineProductionCodeRequest(
        payload
      );

      if (response?.errorMessage) {
        onError?.(response?.errorMessage);
      }

      return response?.data;
    },
    onError,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        predicate(query) {
          return (
            query?.queryKey?.[1] === "productionCodeRequestDetails" &&
            query?.queryKey?.[0] === "production_code"
          );
        },
      });

      onSuccess?.();
    },
    retry: false,
  });
}
